import React, { useEffect, useState, useCallback } from "react";
import api, { API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import DateRangeFilter, { rangeFor } from "@/components/DateRangeFilter";
import { FieldInput } from "@/components/CrudPage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const today = () => new Date().toISOString().slice(0, 10);
const tooltipStyle = { backgroundColor: "#111827", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 };
const TICK = { fontSize: 10, fill: "#9CA3AF" };
const LEGEND_STYLE = { fontSize: 11 };
const MAINT_TYPES = ["Planlı Bakım", "Önleyici Bakım", "Arıza Bakımı", "Kalıp Bakımı"].map((v) => ({ value: v, label: v }));

function MaintenanceForm({ meta, onSaved }) {
  const [form, setForm] = useState({ date: today(), machine_code: "", maintenance_type: "Planlı Bakım", duration_minutes: "", description: "" });
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (n, v) => setForm((f) => ({ ...f, [n]: v }));

  const addImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd);
      setImages((imgs) => [...imgs, r.data.path]);
      toast.success("Resim yüklendi");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!form.date || !form.description) { toast.error("Tarih ve açıklama zorunludur"); return; }
    setSaving(true);
    try {
      const payload = { ...form, image_paths: images };
      if (!payload.machine_code) delete payload.machine_code;
      await api.post("/maintenance-records", payload);
      toast.success("Bakım kaydı eklendi");
      setForm({ date: today(), machine_code: "", maintenance_type: "Planlı Bakım", duration_minutes: "", description: "" });
      setImages([]);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="maintenance-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Tarih *</Label>
          <FieldInput field={{ type: "date", name: "date" }} value={form.date} onChange={set} testid="maintenance-date" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Makine</Label>
          <FieldInput field={{ name: "machine_code", type: "select", options: meta.machines.map((m) => ({ value: m.code, label: `${m.code} — ${m.name}` })) }}
            value={form.machine_code} onChange={set} testid="maintenance-machine" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Bakım Tipi</Label>
          <FieldInput field={{ name: "maintenance_type", type: "select", options: MAINT_TYPES }}
            value={form.maintenance_type} onChange={set} testid="maintenance-type" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Süre (dk)</Label>
          <FieldInput field={{ name: "duration_minutes", type: "number" }} value={form.duration_minutes} onChange={set} testid="maintenance-duration" />
        </div>
        <div className="space-y-1.5 col-span-2 md:col-span-4">
          <Label className="text-xs">Yapılan İşler / Açıklama *</Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
            data-testid="maintenance-description" placeholder="Yapılan bakım işlemlerinin detayı..." className="min-h-[70px] text-sm" />
        </div>
        <div className="space-y-1.5 col-span-2 md:col-span-4">
          <Label className="text-xs">Fotoğraflar</Label>
          <div className="flex flex-wrap items-center gap-3">
            {images.map((p) => (
              <div key={p} className="relative">
                <img src={`${API}/files/${p}`} alt="bakım" className="w-16 h-16 object-cover rounded border border-border" />
                <button onClick={() => setImages((imgs) => imgs.filter((x) => x !== p))}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-white text-xs leading-none"
                  data-testid={`maintenance-image-remove-${p.split("/").pop()}`}>×</button>
              </div>
            ))}
            <label className="w-16 h-16 rounded border border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-accent transition-colors duration-150"
              data-testid="maintenance-image-add">
              <Plus size={18} className="text-muted-foreground" />
              <input type="file" accept="image/*" className="hidden" onChange={addImage} />
            </label>
            {uploading && <span className="text-xs text-muted-foreground">Yükleniyor...</span>}
          </div>
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="maintenance-save">
        {saving ? "Kaydediliyor..." : "Bakım Kaydını Kaydet"}
      </Button>
    </div>
  );
}

export default function Maintenance() {
  const [meta, setMeta] = useState({ machines: [] });
  const [records, setRecords] = useState([]);
  const [range, setRange] = useState(() => rangeFor({ key: "3m", months: 3 }));
  const [impact, setImpact] = useState([]);

  const load = useCallback(() => {
    api.get("/meta/options").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/maintenance-records?limit=50").then((r) => setRecords(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/analytics/maintenance-impact?start=${range.start}&end=${range.end}`)
      .then((r) => setImpact(r.data)).catch(() => {});
  }, [range]);

  const remove = async (id) => {
    if (!window.confirm("Bakım kaydı silinsin mi?")) return;
    try { await api.delete(`/maintenance-records/${id}`); toast.success("Kayıt silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="maintenance-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Bakım Yönetimi</h1>
        <p className="text-sm text-muted-foreground mt-1">Planlı bakım kayıtları, fotoğraflar ve bakımın duruş/verimlilik üzerindeki etkisi</p>
      </div>

      <Card className="p-5 bg-card border-border">
        <h3 className="text-lg font-medium mb-4">Yeni Bakım Kaydı</h3>
        <MaintenanceForm meta={meta} onSaved={load} />
      </Card>

      <Card className="p-4 bg-card border-border" data-testid="maintenance-impact-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-medium">Bakım Etki Analizi — Aylık Trend</h3>
          <DateRangeFilter value={range} onChange={setRange} testidPrefix="maintenance-date" />
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Bakım sayısı/süresi (bar) ile üretim duruşu ve verimlilik (çizgi) karşılaştırması — bakım artışının duruşları azaltıp verimliliği artırıp artırmadığını gösterir.
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={impact}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
            <XAxis dataKey="month" tick={TICK} />
            <YAxis yAxisId="left" tick={TICK} />
            <YAxis yAxisId="right" orientation="right" domain={[0, 120]} tick={TICK} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Bar yAxisId="left" dataKey="maintenance_minutes" name="Bakım Süresi (dk)" fill="#2563EB" radius={[3, 3, 0, 0]} />
            <Line yAxisId="left" dataKey="downtime" name="Duruş (dk)" stroke="#D97706" strokeWidth={2} dot={false} />
            <Line yAxisId="right" dataKey="efficiency" name="Verimlilik %" stroke="#DC2626" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      <div className="space-y-2" data-testid="maintenance-records-list">
        <h3 className="text-lg font-medium">Bakım Kayıtları</h3>
        {records.map((r) => (
          <Card key={r.id} className="p-4 bg-card border-border" data-testid={`maintenance-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-blue-600/20 text-blue-400 border-0 hover:bg-blue-600/20">{r.maintenance_type}</Badge>
                  <span className="font-mono text-xs text-muted-foreground">{r.date}</span>
                  {r.machine_code && <span className="text-xs font-medium">{r.machine_code}</span>}
                  {r.duration_minutes ? <span className="text-xs text-muted-foreground font-mono">{r.duration_minutes} dk</span> : null}
                </div>
                <p className="text-sm mt-1.5 text-foreground/90">{r.description}</p>
                {r.image_paths?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {r.image_paths.map((p) => (
                      <img key={p} src={`${API}/files/${p}`} alt="bakım" className="w-16 h-16 object-cover rounded border border-border" />
                    ))}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" data-testid={`maintenance-delete-${r.id}`} onClick={() => remove(r.id)}>
                <Trash2 size={14} />
              </Button>
            </div>
          </Card>
        ))}
        {records.length === 0 && <p className="text-sm text-muted-foreground">Bakım kaydı yok</p>}
      </div>
    </div>
  );
}
